import { useEffect, useState } from 'react';
import { Button } from '../../components/common/Button';
import { Spinner } from '../../components/common/Spinner';
import { useImportEntities, useUploadImportCsv } from '../../hooks/useAdminImports';
import type { CsvImportResult } from '../../services/api/adminImports';

/**
 * Phase 99 (Plan 99-01) — /admin/imports. Bulk CSV import for entities
 * enabled and implemented on this instance (see `GET /admin/imports/entities`,
 * server-driven so a projects-disabled instance never offers a
 * projects-module importer). Dry-run/preview UX mirrors
 * `ImportEntriesModal` (time-entry import).
 */
export default function DataImportPage() {
  const entitiesQuery = useImportEntities();
  const uploadMutation = useUploadImportCsv();

  const entities = entitiesQuery.data?.data ?? [];
  const [selectedEntity, setSelectedEntity] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<CsvImportResult | null>(null);

  // Default to the first available entity once the list loads.
  const firstEntity = entities[0]?.entity;
  useEffect(() => {
    if (!selectedEntity && firstEntity) {
      setSelectedEntity(firstEntity);
    }
  }, [firstEntity, selectedEntity]);

  const selectedDescriptor = entities.find((e) => e.entity === selectedEntity);

  // Client-side CSV template: one header row with required + optional columns
  // for the selected entity, straight from the descriptor the backend already
  // serves — no extra endpoint, always in sync with the registry.
  const handleDownloadTemplate = () => {
    if (!selectedDescriptor) return;
    const header = [...selectedDescriptor.requiredColumns, ...selectedDescriptor.optionalColumns].join(',');
    const blob = new Blob([`${header}\n`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${selectedDescriptor.entity}-import-template.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const resetForNewAttempt = (entity: string) => {
    setSelectedEntity(entity);
    setFile(null);
    setResult(null);
    uploadMutation.reset();
  };

  const handlePreview = () => {
    if (!selectedEntity || !file) return;
    uploadMutation.mutate(
      { entity: selectedEntity, file, dryRun: true },
      { onSuccess: (response) => setResult(response.data) }
    );
  };

  const handleImport = () => {
    if (!selectedEntity || !file) return;
    uploadMutation.mutate(
      { entity: selectedEntity, file, dryRun: false },
      { onSuccess: (response) => setResult(response.data) }
    );
  };

  const canImport = Boolean(result?.dryRun && result.imported > 0);

  return (
    <div className="p-6 max-w-3xl">
      <header className="mb-4">
        <h1 className="text-2xl font-semibold">Data Import</h1>
        <p className="text-sm text-gray-600 mt-1">
          Every import runs as a preview first. A preview writes nothing — review the row counts
          and problems below, then click Import to commit.
        </p>
      </header>

      {entitiesQuery.isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="import-entity">
              Entity
            </label>
            <select
              id="import-entity"
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
              value={selectedEntity}
              onChange={(e) => resetForNewAttempt(e.target.value)}
            >
              {entities.map((entity) => (
                <option key={entity.entity} value={entity.entity}>
                  {entity.label}
                </option>
              ))}
            </select>
          </div>

          {selectedDescriptor && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-gray-700">CSV columns for {selectedDescriptor.label}</p>
                <button
                  type="button"
                  onClick={handleDownloadTemplate}
                  className="text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline"
                >
                  Download template
                </button>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wider text-gray-500 mb-1">Required</p>
                  <ul className="list-disc list-inside text-gray-700">
                    {selectedDescriptor.requiredColumns.map((col) => (
                      <li key={col}>{col}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wider text-gray-500 mb-1">Optional</p>
                  {selectedDescriptor.optionalColumns.length === 0 ? (
                    <p className="text-gray-400">None</p>
                  ) : (
                    <ul className="list-disc list-inside text-gray-700">
                      {selectedDescriptor.optionalColumns.map((col) => (
                        <li key={col}>{col}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          )}

          {!result && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">CSV file</label>
              <input
                type="file"
                accept=".csv"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
            </div>
          )}

          {uploadMutation.isError && !result && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-800">
                {uploadMutation.error instanceof Error
                  ? uploadMutation.error.message
                  : 'Import failed. Please try again.'}
              </p>
            </div>
          )}

          {result && (
            <div className="space-y-4">
              <div
                className={`${result.dryRun ? 'bg-blue-50 border-blue-200' : 'bg-green-50 border-green-200'} border rounded-lg p-3`}
              >
                <p className={`text-sm font-medium ${result.dryRun ? 'text-blue-800' : 'text-green-800'}`}>
                  {result.dryRun ? (
                    <>
                      <strong>{result.imported}</strong> rows will be imported.
                    </>
                  ) : (
                    <>
                      Successfully imported <strong>{result.imported}</strong> rows.
                    </>
                  )}
                </p>
              </div>

              {!result.dryRun && (
                <p className="text-xs text-gray-500">
                  Rows are written one at a time, so a failure part-way through leaves earlier rows
                  imported and reports the rest as problems.
                </p>
              )}

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <p className="text-sm font-medium text-gray-700 mb-2">Breakdown:</p>
                <dl className="text-sm text-gray-600 space-y-1">
                  <div className="flex justify-between">
                    <dt>Rows in file</dt>
                    <dd className="font-medium text-gray-900">{result.totalRows}</dd>
                  </div>
                  {result.rowsSkipped > 0 && (
                    <div className="flex justify-between">
                      <dt>Rows skipped</dt>
                      <dd className="font-medium text-amber-600">{result.rowsSkipped}</dd>
                    </div>
                  )}
                  <div className="flex justify-between border-t border-gray-300 pt-1 mt-1">
                    <dt className="font-medium text-gray-900">{result.dryRun ? 'To import' : 'Imported'}</dt>
                    <dd className="font-bold text-gray-900">{result.imported}</dd>
                  </div>
                </dl>
              </div>

              {result.problems.length > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-sm font-medium text-yellow-800 mb-2">Problems:</p>
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="text-left text-yellow-700">
                        <th className="pr-4 py-1">Row</th>
                        <th className="pr-4 py-1">Reason</th>
                        <th className="py-1">Detail</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.problems.map((problem, idx) => (
                        <tr key={`${problem.row}-${idx}`} className="text-yellow-700">
                          <td className="pr-4 py-1">{problem.row}</td>
                          <td className="pr-4 py-1">{problem.reason}</td>
                          <td className="py-1">{problem.detail ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-3">
            {result ? (
              result.dryRun ? (
                <>
                  <Button variant="secondary" onClick={() => resetForNewAttempt(selectedEntity)}>
                    Cancel
                  </Button>
                  <Button onClick={handleImport} disabled={!canImport} isLoading={uploadMutation.isPending}>
                    Import
                  </Button>
                </>
              ) : (
                <Button variant="secondary" onClick={() => resetForNewAttempt(selectedEntity)}>
                  Close
                </Button>
              )
            ) : (
              <Button onClick={handlePreview} disabled={!selectedEntity || !file} isLoading={uploadMutation.isPending}>
                Preview
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
